// Navigation targets that come from stored content (banner records, query
// parameters) are untrusted input. Assigning one straight to window.location or
// an <a href> lets whoever authored it choose the scheme, which is how
// "javascript:" and open-redirect payloads get in.

const SAFE_PROTOCOLS = ["http:", "https:", "mailto:"];

// Hosts an absolute banner URL is allowed to point at. Anything else is
// treated as untrusted and dropped.
export const ALLOWED_EXTERNAL_HOSTS = [
  "pajak.ind",
  "www.pajak.ind",
  "pajakind.com",
  "www.pajakind.com",
  "app.pajakind.com",
  "blog.pajakind.com",
];

const isAllowedHost = (hostname, allowedHosts) =>
  allowedHosts.some(
    (allowed) =>
      hostname === allowed || hostname.endsWith(`.${allowed}`),
  );

// Returns a safe href, or null when the target cannot be trusted.
// - relative paths stay relative (same-origin by construction)
// - absolute URLs must use a safe scheme AND an allowlisted host
//
// `allowAnyHttpsHost` relaxes the host check for admin-authored content
// (banners), where the destination is arbitrary by design. The scheme check
// still applies, so "javascript:" and "data:" stay blocked; plain http and
// protocol-relative URLs stay blocked too.
export const getSafeNavigationUrl = (
  rawUrl,
  { allowedHosts = ALLOWED_EXTERNAL_HOSTS, allowAnyHttpsHost = false } = {},
) => {
  if (typeof rawUrl !== "string") return null;

  const url = rawUrl.trim();
  if (!url) return null;

  // Protocol-relative ("//evil.example") resolves cross-origin — reject it.
  if (url.startsWith("//")) return null;

  // A same-origin relative path. Reject anything that tries to sneak a scheme
  // in before the first slash.
  if (url.startsWith("/")) {
    return url;
  }

  let parsed;
  try {
    parsed = new URL(url, window.location.origin);
  } catch (error) {
    return null;
  }

  if (!SAFE_PROTOCOLS.includes(parsed.protocol)) {
    return null;
  }

  if (parsed.origin === window.location.origin) {
    return parsed.href;
  }

  if (parsed.protocol === "mailto:") {
    return parsed.href;
  }

  if (allowAnyHttpsHost && parsed.protocol === "https:") {
    return parsed.href;
  }

  return isAllowedHost(parsed.hostname, allowedHosts) ? parsed.href : null;
};

// Same check, restricted to in-app paths. Used for redirect targets that should
// never leave the app (e.g. a returnTo query parameter).
export const getSafeInternalPath = (rawPath, fallback = null) => {
  if (typeof rawPath !== "string") return fallback;

  const path = rawPath.trim();
  if (!path.startsWith("/") || path.startsWith("//")) return fallback;

  return path;
};
